import { useCallback, useId, useState } from "react"
import type { KeyboardEvent } from "react"
import { Add, Close, LockOutlined } from "@mui/icons-material"
import { Alert, Box, Button, Chip, TextField, Typography, chipClasses, inputBaseClasses } from "@mui/material"
import type { SxProps, Theme } from "@mui/material"
import { MUTED_SX } from "./card-styles"
import { isHostCovered, parseHostEntries } from "./host-entries"
import type { HostEntryMessage } from "./host-entries"

const SECTION_SX = { display: "flex", flexDirection: "column", gap: 1.5 } satisfies SxProps<Theme>
const SUGGESTED_SX = { display: "flex", flexDirection: "column", gap: 1 } satisfies SxProps<Theme>
const ADD_ROW_SX = { display: "flex", alignItems: "stretch", gap: 1 } satisfies SxProps<Theme>
const INPUT_SX = { flex: 1, [`& .${inputBaseClasses.input}`]: { fontFamily: "monospace" } } satisfies SxProps<Theme>
const ADD_BUTTON_SX = { flexShrink: 0, textTransform: "none", whiteSpace: "nowrap" } satisfies SxProps<Theme>
const INVALID_MESSAGE_SX = { color: "error.main" } satisfies SxProps<Theme>
const CHIP_LIST_SX = { listStyle: "none", m: 0, p: 0, display: "flex", flexWrap: "wrap", gap: 1 } satisfies SxProps<Theme>
const DOMAIN_CHIP_SX = {
  fontFamily: "monospace",
  bgcolor: "var(--app-opcolor-grey-lighter)",
  color: "var(--app-text-primary)",
} satisfies SxProps<Theme>
const LOCKED_CHIP_SX = {
  ...DOMAIN_CHIP_SX,
  bgcolor: "var(--app-primary-tint)",
  color: "var(--app-blue-dark-on-light-surface)",
  [`& .${chipClasses.icon}`]: { color: "inherit" },
} satisfies SxProps<Theme>
const SUGGESTIONS_SX = { display: "flex", flexWrap: "wrap", gap: 1 } satisfies SxProps<Theme>

export interface AllowedDomainsSectionProps {
  allowedHosts: string[]
  lockedHosts: string[]
  suggestedHosts: string[]
  isSaving: boolean
  labelledBy: string
  onAllowedHostsChange: (to: string[]) => Promise<boolean>
}

export function AllowedDomainsSection({
  allowedHosts,
  lockedHosts,
  suggestedHosts,
  isSaving,
  labelledBy,
  onAllowedHostsChange,
}: AllowedDomainsSectionProps) {
  const suggestedTitleId = useId()
  const [entry, setEntry] = useState("")
  const [messages, setMessages] = useState<HostEntryMessage[]>([])

  const suggestions = suggestedHosts.filter((host) => !isHostCovered(host, { allowedHosts, lockedHosts }))

  const addEntries = useCallback(() => {
    if (isSaving) return
    const { added, messages: entryMessages, invalid } = parseHostEntries(entry, { allowedHosts, lockedHosts })
    setMessages(entryMessages)
    const remaining = invalid.join(", ")
    setEntry(remaining)
    if (added.length === 0) return
    void onAllowedHostsChange([...allowedHosts, ...added]).then((saved) => {
      // Bring the typed text back unless the admin edited the field while the save ran.
      if (!saved) setEntry((current) => (current === remaining ? entry : current))
    })
  }, [isSaving, entry, allowedHosts, lockedHosts, onAllowedHostsChange])

  const onEntryKeyDown = useCallback((event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== "Enter" || event.nativeEvent.isComposing) return
    event.preventDefault()
    addEntries()
  }, [addEntries])

  const removeHost = (host: string) => {
    if (!isSaving) void onAllowedHostsChange(allowedHosts.filter((allowed) => allowed !== host))
  }

  const addSuggestion = (host: string) => {
    if (!isSaving) void onAllowedHostsChange([...allowedHosts, host])
  }

  return (
    <Box sx={SECTION_SX}>
      <Box>
        <Box sx={ADD_ROW_SX}>
          <TextField
            size="small"
            value={entry}
            placeholder="example.com"
            onChange={(event) => {
              setEntry(event.target.value)
              setMessages([])
            }}
            onKeyDown={onEntryKeyDown}
            slotProps={{ htmlInput: { "aria-labelledby": labelledBy } }}
            sx={INPUT_SX}
          />
          <Button variant="outlined" startIcon={<Add />} onClick={addEntries} sx={ADD_BUTTON_SX}>Add domain</Button>
        </Box>
        <Typography variant="caption" component="p" sx={MUTED_SX}>
          Separate entries with commas. Each domain also allows its subdomains: example.com includes www.example.com.
        </Typography>
        {messages.map((message, index) => (
          // The same entry can be typed twice, so the text alone is not a unique key.
          <Typography key={index} variant="caption" component="p" sx={message.kind === "invalid" ? INVALID_MESSAGE_SX : MUTED_SX}>
            {message.text}
          </Typography>
        ))}
      </Box>
      <Box component="ul" sx={CHIP_LIST_SX} aria-labelledby={labelledBy}>
        {lockedHosts.map((host) => (
          <li key={host}>
            <Chip icon={<LockOutlined />} label={host} sx={LOCKED_CHIP_SX} />
          </li>
        ))}
        {allowedHosts.map((host) => (
          <li key={host}>
            <Chip label={host} deleteIcon={<Close />} onDelete={() => { removeHost(host) }} sx={DOMAIN_CHIP_SX} />
          </li>
        ))}
      </Box>
      {suggestions.length > 0 && (
        <Box sx={SUGGESTED_SX}>
          <Typography id={suggestedTitleId} variant="body2" sx={MUTED_SX}>Suggested</Typography>
          <Box sx={SUGGESTIONS_SX} role="group" aria-labelledby={suggestedTitleId}>
            {suggestions.map((host) => (
              <Chip key={host} variant="outlined" icon={<Add />} label={host} onClick={() => { addSuggestion(host) }} />
            ))}
          </Box>
        </Box>
      )}
      <Alert severity="warning">
        Access to a domain works both ways. Kelly can read from it, and code Kelly runs can also send data to it. Add only sites and services your firm trusts.
      </Alert>
    </Box>
  )
}
